import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ExternalLink, List, LayoutList, Plus } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import type { Event, ScheduleItem } from '@/lib/types';
import { ACTIVITY_LABELS } from '@/lib/types';
import { KULLIYAT, KURAN, KURAN_YOLU, eserAdi } from '@/lib/kulliyat';
import { PageHeader } from '@/components/layout/PageHeader';
import { useYetki } from '@/lib/yetki';
import {
  namazSatirlari,
  otomatikNamazMi,
  programGunleri,
  useNamazVakitleri,
} from '@/lib/namaz';
import { CrudActions } from '@/components/ui/CrudActions';
import { cn, formatDate, formatTime } from '@/lib/utils';
import { Badge, Button, Card, Input, Label, Select, Spinner, Textarea } from '@/components/ui';

type ViewMode = 'list' | 'daily';
type ScheduleTimeStatus = 'past' | 'current' | 'upcoming';

const ACTIVITY_OPTIONS = Object.entries(ACTIVITY_LABELS);
const WEEKDAYS = ['Pt', 'Sa', 'Ça', 'Pe', 'Cu', 'Ct', 'Pz'];

function normalizeTime(time: string): string {
  return time.length >= 5 ? time.slice(0, 5) : time;
}

function getScheduleTimeStatus(item: ScheduleItem, now: Date): ScheduleTimeStatus {
  const start = new Date(`${item.dayDate}T${normalizeTime(item.startTime)}:00`);
  const end = item.endTime
    ? new Date(`${item.dayDate}T${normalizeTime(item.endTime)}:00`)
    : new Date(start.getTime() + 30 * 60 * 1000);

  const t = now.getTime();
  if (t >= end.getTime()) return 'past';
  if (t >= start.getTime()) return 'current';
  return 'upcoming';
}

function useNowTick(intervalMs = 30_000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs]);
  return now;
}

function scheduleCardClass(status: ScheduleTimeStatus) {
  return cn(
    status === 'past' && 'border-muted/60 bg-muted/20',
    status === 'current' && 'border-accent ring-2 ring-accent/40 bg-accent/10 shadow-md',
    status === 'upcoming' && 'border-border',
  );
}

interface ScheduleFormState {
  startTime: string;
  title: string;
  description: string;
  activityType: string;
  /* Kütüphanedeki metnin adresi; boşsa satırda bağlantı çıkmıyor. */
  link: string;
}

const BOS_AKIS: ScheduleFormState = {
  startTime: '',
  title: '',
  description: '',
  activityType: 'reading',
  link: '',
};

function getCalendarDays(yearMonth: string): (string | null)[] {
  const [y, m] = yearMonth.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const last = new Date(y, m, 0);
  const startPad = (first.getDay() + 6) % 7;
  const days: (string | null)[] = Array(startPad).fill(null);
  for (let d = 1; d <= last.getDate(); d++) {
    days.push(`${yearMonth}-${String(d).padStart(2, '0')}`);
  }
  return days;
}

function ScheduleItemForm({
  dayDate,
  form,
  setForm,
  setDayDate,
  editingId,
  error,
  loading,
  onSubmit,
  onCancel,
  minDate,
  maxDate,
}: {
  dayDate: string;
  form: ScheduleFormState;
  setForm: (f: ScheduleFormState) => void;
  setDayDate: (d: string) => void;
  editingId: string | null;
  error: string;
  loading: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  minDate?: string;
  maxDate?: string;
}) {
  return (
    <form onSubmit={onSubmit} className="space-y-3">
      <p className="font-serif text-sm font-semibold text-primary">
        {editingId ? 'Satır Düzenle' : 'Program Satırı Ekle'}
      </p>
      <p className="text-xs text-muted-foreground">
        Bitiş saati otomatik: bir sonraki satırın başlangıcı, bir öncekinin bitişi olur.
      </p>
      <div>
        <Label>Tarih</Label>
        <Input
          type="date"
          value={dayDate}
          min={minDate}
          max={maxDate}
          onChange={(e) => setDayDate(e.target.value)}
          required
        />
        {minDate && maxDate && (
          <p className="mt-1 text-xs text-muted-foreground">
            Program tarih aralığı: {formatDate(minDate)} – {formatDate(maxDate)}
          </p>
        )}
      </div>
      <div>
        <Label>Başlangıç Saati</Label>
        <Input
          type="time"
          value={form.startTime}
          onChange={(e) => setForm({ ...form, startTime: e.target.value })}
          required
        />
      </div>
      <div>
        <Label>Etkinlik</Label>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          required
          placeholder="ör. Okuma, Sabah Namazı"
        />
      </div>
      <div>
        <Label>Tür</Label>
        <Select
          value={form.activityType}
          onChange={(e) => {
            const tur = e.target.value;
            /* Kur'an okumaya geçilince, bağlantı boşsa Kur'an-ı Kerim
               kendiliğinden konuyor; seçilmiş bir bağlantı varsa
               dokunulmuyor. */
            const link = tur === 'quran_reading' && !form.link ? KURAN_YOLU : form.link;
            setForm({ ...form, activityType: tur, link });
          }}
        >
          {ACTIVITY_OPTIONS.map(([key, label]) => (
            <option key={key} value={key}>
              {label}
            </option>
          ))}
        </Select>
      </div>
      {/* Akışta yalnız "Okuma" yazınca ne okunacağı belli olmuyordu.
          Satır kütüphanedeki metne bağlanıyor; katılımcı dokununca
          doğrudan o esere gidiyor. */}
      <div>
        <Label>Kütüphane bağlantısı</Label>
        <Select value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })}>
          <option value="">— yok —</option>
          <optgroup label="Kur'an ve evrad">
            {KURAN.map((e2) => (
              <option key={e2.yol} value={e2.yol}>
                {e2.ad}
              </option>
            ))}
          </optgroup>
          <optgroup label="Külliyat — Envâr Neşriyat 2005">
            {KULLIYAT.map((e2) => (
              <option key={e2.yol} value={e2.yol}>
                {e2.ad}
              </option>
            ))}
          </optgroup>
        </Select>
        <p className="mt-1 text-xs text-muted-foreground">
          Seçilen eser risaletinnur.com'da yeni sekmede açılır.
        </p>
      </div>
      <div>
        <Label>Not (opsiyonel)</Label>
        <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button type="submit" variant="gold" disabled={loading}>
          {loading ? <Spinner /> : editingId ? 'Kaydet' : 'Ekle'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          İptal
        </Button>
      </div>
    </form>
  );
}

function ScheduleItemDisplay({
  item,
  showDate,
  timeStatus,
  onEdit,
  onDelete,
  duzenlenebilir,
}: {
  item: ScheduleItem;
  showDate?: boolean;
  timeStatus?: ScheduleTimeStatus;
  onEdit: () => void;
  onDelete: () => void;
  /* Katılımcıya akış okunur geliyor; kalem ve çöp kutusu yok. */
  duzenlenebilir?: boolean;
}) {
  /* Namaz satırları takvimden geliyor, kaydedilmiş bir satır değil:
     düzenlenmiyor, silinmiyor. */
  const namaz = otomatikNamazMi(item);
  return (
    <div
      className={cn(
        'flex items-start justify-between gap-3 rounded-lg transition-colors',
        timeStatus === 'past' && 'opacity-55',
        timeStatus === 'current' && 'rounded-lg',
      )}
    >
      <div className="flex gap-3">
        {timeStatus === 'current' && (
          <div className="mt-1 w-1 shrink-0 self-stretch rounded-full bg-accent" aria-hidden />
        )}
        <div>
          {showDate && <p className="text-xs text-muted-foreground">{formatDate(item.dayDate)}</p>}
          <p
            className={cn(
              'text-sm font-medium',
              timeStatus === 'current' ? 'text-accent font-semibold' : 'text-accent',
              timeStatus === 'past' && 'text-muted-foreground line-through decoration-muted-foreground/50',
            )}
          >
            {formatTime(item.startTime)}
            {item.endTime && ` – ${formatTime(item.endTime)}`}
          </p>
          <h4
            className={cn(
              'font-serif font-semibold',
              timeStatus === 'past' ? 'text-muted-foreground line-through' : 'text-primary',
              timeStatus === 'current' && 'text-primary',
            )}
          >
            {item.title}
          </h4>
          {item.description && (
            <p className={cn('mt-1 text-xs', timeStatus === 'past' ? 'text-muted-foreground/70' : 'text-muted-foreground')}>
              {item.description}
            </p>
          )}
          {item.link && (
            <a
              href={item.link}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-1.5 inline-flex items-center gap-1 text-xs text-primary underline"
            >
              <ExternalLink className="h-3 w-3" aria-hidden />
              {eserAdi(item.link) ?? 'Kütüphanede aç'}
            </a>
          )}
        </div>
      </div>
      <div className="flex flex-col items-end gap-2">
        {timeStatus === 'current' && <Badge variant="gold">Şimdi</Badge>}
        {timeStatus === 'past' && <Badge variant="default">Geçti</Badge>}
        <Badge variant="gold">{ACTIVITY_LABELS[item.activityType] ?? item.activityType}</Badge>
        {duzenlenebilir && !namaz && <CrudActions onEdit={onEdit} onDelete={onDelete} />}
      </div>
    </div>
  );
}

function ScheduleCalendar({
  schedule,
  selectedDay,
  calendarMonth,
  onSelectDay,
  onChangeMonth,
  minDate,
  maxDate,
}: {
  schedule: ScheduleItem[];
  selectedDay: string;
  calendarMonth: string;
  onSelectDay: (day: string) => void;
  onChangeMonth: (month: string) => void;
  minDate?: string;
  maxDate?: string;
}) {
  const daysWithSchedule = useMemo(() => new Set(schedule.map((s) => s.dayDate)), [schedule]);
  const calendarDays = useMemo(() => getCalendarDays(calendarMonth), [calendarMonth]);

  function shiftMonth(delta: number) {
    const [y, m] = calendarMonth.split('-').map(Number);
    const next = new Date(y, m - 1 + delta, 1);
    onChangeMonth(
      `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}`,
    );
  }

  const monthLabel = new Date(`${calendarMonth}-01`).toLocaleDateString('tr-TR', {
    month: 'long',
    year: 'numeric',
  });

  return (
    <Card>
      <div className="mb-3 flex items-center justify-between">
        <Button type="button" size="sm" variant="outline" onClick={() => shiftMonth(-1)}>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <p className="font-serif font-semibold capitalize text-primary">{monthLabel}</p>
        <Button type="button" size="sm" variant="outline" onClick={() => shiftMonth(1)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center text-[10px] font-medium text-muted-foreground">
        {WEEKDAYS.map((d) => (
          <div key={d} className="py-1">
            {d}
          </div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-7 gap-1">
        {calendarDays.map((day, idx) => {
          if (!day) return <div key={`empty-${idx}`} />;
          const hasSchedule = daysWithSchedule.has(day);
          const isSelected = selectedDay === day;
          const dayNum = parseInt(day.slice(8), 10);
          const inRange = (!minDate || day >= minDate) && (!maxDate || day <= maxDate);
          const disabled = !inRange;

          return (
            <button
              key={day}
              type="button"
              disabled={disabled}
              onClick={() => !disabled && onSelectDay(day)}
              className={`relative flex aspect-square flex-col items-center justify-center rounded-lg text-sm transition-colors ${
                disabled
                  ? 'cursor-not-allowed text-muted-foreground/30'
                  : isSelected
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : hasSchedule
                      ? 'bg-accent/15 font-semibold text-primary hover:bg-accent/25'
                      : 'text-muted-foreground hover:bg-muted/50'
              }`}
            >
              {dayNum}
              {hasSchedule && !isSelected && (
                <span className="absolute bottom-1 h-1 w-1 rounded-full bg-accent" />
              )}
            </button>
          );
        })}
      </div>
    </Card>
  );
}

export function EventDetailPage() {
  /* Adres artık etkinliğin kendi kimliğini taşıyor. Adı değişince bağ
     kırılmasın, aynı adı taşıyan iki program çakışmasın diye. Eskiden
     paylaşılmış slug'lı bağlar da çalışsın istediğimizden ikisine de
     bakılıyor. */
  const { eventRef } = useParams<{ eventRef: string }>();
  const [view, setView] = useState<ViewMode>('list');
  const now = useNowTick();
  const [selectedDay, setSelectedDay] = useState('');
  const [calendarMonth, setCalendarMonth] = useState(new Date().toISOString().slice(0, 7));
  const [dayDate, setDayDate] = useState(new Date().toISOString().slice(0, 10));
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<ScheduleFormState>(BOS_AKIS);

  const { data: orgs } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api<{ organizations: { id: string }[] }>('/organizations'),
  });

  const orgId = orgs?.organizations[0]?.id;
  /* Program akışını yönetici kurar. Katılımcıya satır ekleme, düzenleme
     ve silme düğmeleri gösteriliyordu; API zaten reddediyordu ama
     ekranda yetkisi varmış gibi duruyordu. */
  const { yonetebilir } = useYetki(orgId);

  const { data: eventsData, isLoading: eventsLoading } = useQuery({
    queryKey: ['events', orgId],
    queryFn: () => api<{ events: Event[] }>(`/events/org/${orgId}`),
    enabled: !!orgId,
  });

  const event = eventsData?.events.find((e) => e.id === eventRef || e.slug === eventRef);

  const { data: scheduleData, isLoading: scheduleLoading, refetch } = useQuery({
    queryKey: ['schedule', event?.id],
    queryFn: () => api<{ schedule: ScheduleItem[] }>(`/schedule/${event!.id}`),
    enabled: !!event?.id,
  });

  const kayitliAkis = useMemo(() => scheduleData?.schedule ?? [], [scheduleData]);

  /* Namaz vakitleri akışa gösterimde katılıyor, veritabanına yazılmıyor:
     program hiç kurulmamış olsa da vakitler görünsün, tarih değişince
     kendiliğinden kaysın, şablon ikinci kez yüklenince çift satır
     çıkmasın diye. Elle "Öğle Namazı" girilmişse o gün için üretilmiyor. */
  const { data: vakitler } = useNamazVakitleri(event);
  const schedule = useMemo(() => {
    if (!event?.id) return kayitliAkis;
    const gunler = programGunleri(event);
    if (!gunler.length) return kayitliAkis;
    const namazlar = namazSatirlari(vakitler, gunler, event.id, kayitliAkis);
    return [...kayitliAkis, ...namazlar].sort(
      (a, b) =>
        a.dayDate.localeCompare(b.dayDate) ||
        a.startTime.localeCompare(b.startTime) ||
        a.sortOrder - b.sortOrder,
    );
  }, [kayitliAkis, vakitler, event]);

  const days = useMemo(() => [...new Set(schedule.map((s) => s.dayDate))].sort(), [schedule]);

  useEffect(() => {
    if (days.length && !selectedDay) {
      setSelectedDay(days[0]);
      setCalendarMonth(days[0].slice(0, 7));
    }
    if (event?.startDate) {
      setDayDate(event.startDate);
      if (!selectedDay) setSelectedDay(event.startDate);
    } else if (event?.startDate && !days.length) {
      setDayDate(event.startDate);
    }
  }, [days, selectedDay, event?.startDate]);

  const filteredSchedule = useMemo(() => {
    if (view === 'daily' && selectedDay) {
      return schedule.filter((s) => s.dayDate === selectedDay);
    }
    return schedule;
  }, [schedule, view, selectedDay]);

  function resetForm() {
    setForm(BOS_AKIS);
    setEditingId(null);
    setShowAdd(false);
    setError('');
  }

  async function seedBarla() {
    if (!event?.id) return;
    setLoading(true);
    setError('');
    try {
      await api(`/schedule/${event.id}/seed-barla`, {
        method: 'POST',
        body: JSON.stringify({ dayDate }),
      });
      await refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Şablon yüklenemedi');
    } finally {
      setLoading(false);
    }
  }

  async function saveItem(e: React.FormEvent) {
    e.preventDefault();
    if (!event?.id) return;
    setLoading(true);
    setError('');
    try {
      const payload = {
        dayDate,
        startTime: form.startTime,
        title: form.title,
        description: form.description || undefined,
        activityType: form.activityType,
        /* Boş dizge gönderiliyor: sunucu bunu "bağlantıyı kaldır"
           diye okuyor, alan hiç gelmezse eskisini koruyor. */
        link: form.link,
      };
      if (editingId) {
        await api(`/schedule/${event.id}/${editingId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await api(`/schedule/${event.id}`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }
      resetForm();
      await refetch();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    } finally {
      setLoading(false);
    }
  }

  async function deleteItem(itemId: string) {
    if (!event?.id || !confirm('Bu satırı silmek istediğinize emin misiniz?')) return;
    if (editingId === itemId) resetForm();
    await api(`/schedule/${event.id}/${itemId}`, { method: 'DELETE' });
    await refetch();
  }

  function startEdit(item: ScheduleItem) {
    setEditingId(item.id);
    setShowAdd(false);
    setDayDate(item.dayDate);
    setForm({
      startTime: item.startTime.slice(0, 5),
      title: item.title,
      description: item.description ?? '',
      activityType: item.activityType,
      link: item.link ?? '',
    });
  }

  if (eventsLoading || scheduleLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  if (!event) {
    return (
      <Card>
        <p>Program bulunamadı</p>
        <Link to="/events">
          <Button variant="outline" className="mt-4">
            Geri Dön
          </Button>
        </Link>
      </Card>
    );
  }

  const minDate = event.startDate;
  const maxDate = event.endDate;

  return (
    <div className="space-y-4">
      <PageHeader title={event.name} subtitle="Günlük program akışı" backTo="/events" />

      <Card>
        {event.description && <p className="text-sm text-muted-foreground">{event.description}</p>}
        <div className="mt-3 flex flex-wrap gap-2">
          {event.startDate && (
            <Badge variant="gold">
              {formatDate(event.startDate)}
              {event.endDate && event.endDate !== event.startDate && ` – ${formatDate(event.endDate)}`}
            </Badge>
          )}
          {event.locationName && <Badge>{event.locationName}</Badge>}
        </div>
        {event.locationDirections && (
          <p className="mt-2 text-sm text-muted-foreground">{event.locationDirections}</p>
        )}
        {event.accommodationInfo && (
          <p className="mt-2 text-sm text-muted-foreground">
            <span className="font-medium text-primary">Konaklama:</span> {event.accommodationInfo}
          </p>
        )}
        {event.notes && (
          <p className="mt-2 text-sm text-muted-foreground">
            <span className="font-medium text-primary">Not:</span> {event.notes}
          </p>
        )}
        {event.locationMapsUrl && (
          <a
            href={event.locationMapsUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 inline-flex items-center gap-1 text-sm text-primary underline"
          >
            <ExternalLink className="h-4 w-4" />
            Yol Tarifi
          </a>
        )}
        {yonetebilir && (
          <div className="mt-3">
            <Link to={`/event/${event.id}/edit`}>
              <Button size="sm" variant="outline">
                Program Bilgilerini Düzenle
              </Button>
            </Link>
          </div>
        )}
      </Card>

      {/* Ekleme açıkken görünüm düğmeleri de duruyordu; ekranda hem form
          hem liste olunca hangisine bakılacağı belirsizdi. Form açıkken
          yalnız form kalıyor. */}
      {!showAdd && (
        <div className="flex flex-wrap gap-2">
          {(['list', 'daily'] as ViewMode[]).map((mode) => (
            <Button
              key={mode}
              size="sm"
              variant={view === mode ? 'default' : 'outline'}
              onClick={() => setView(mode)}
            >
              {mode === 'list' && <List className="h-4 w-4" />}
              {mode === 'daily' && <LayoutList className="h-4 w-4" />}
              {mode === 'list' ? 'Liste' : 'Günlük'}
            </Button>
          ))}
          {yonetebilir && (
            <Button
              size="sm"
              variant="gold"
              onClick={() => {
                resetForm();
                setShowAdd(true);
              }}
            >
              <Plus className="h-4 w-4" />
              Ekle
            </Button>
          )}
        </div>
      )}

      {!showAdd && view === 'daily' && schedule.length > 0 && (
        <ScheduleCalendar
          schedule={schedule}
          selectedDay={selectedDay}
          calendarMonth={calendarMonth}
          onSelectDay={(day) => {
            setSelectedDay(day);
            setCalendarMonth(day.slice(0, 7));
          }}
          onChangeMonth={setCalendarMonth}
          minDate={minDate}
          maxDate={maxDate}
        />
      )}

      {showAdd && !editingId && (
        <Card>
          <ScheduleItemForm
            dayDate={dayDate}
            form={form}
            setForm={setForm}
            setDayDate={setDayDate}
            editingId={null}
            error={error}
            loading={loading}
            onSubmit={saveItem}
            onCancel={resetForm}
            minDate={minDate}
            maxDate={maxDate}
          />
        </Card>
      )}

      {/* Akışta yalnız namaz vakitleri varken de şablon teklif ediliyor.
          Şablonu ancak yönetici yükleyebiliyor; katılımcıya bu kutu
          gösterilmiyordu ama düğme duruyordu. */}
      {!showAdd && kayitliAkis.length === 0 && yonetebilir && (
        <Card className="text-center">
          <p className="font-serif text-primary">Henüz program akışı yok</p>
          <p className="mt-2 text-sm text-muted-foreground">
            {schedule.length > 0
              ? 'Aşağıdaki namaz vakitleri Diyanet takviminden geliyor. Günün kalanını Barla Nisan Okuması şablonuyla doldurabilirsiniz.'
              : 'Barla Nisan Okuması şablonunu tek tıkla yükleyebilirsiniz.'}
          </p>
          <div className="mt-4 space-y-2">
            <Input
              type="date"
              value={dayDate}
              min={minDate}
              max={maxDate}
              onChange={(e) => setDayDate(e.target.value)}
              className="mx-auto max-w-xs"
            />
            <Button variant="gold" onClick={seedBarla} disabled={loading}>
              {loading ? <Spinner /> : 'Barla Program Şablonunu Yükle'}
            </Button>
          </div>
          {error && <p className="mt-2 text-sm text-destructive">{error}</p>}
        </Card>
      )}

      {showAdd && !editingId ? null : schedule.length === 0 ? (
        !yonetebilir && (
          <Card className="text-center text-sm text-muted-foreground">
            Henüz program akışı yok.
          </Card>
        )
      ) : view === 'daily' ? (
        <div className="space-y-3">
          {selectedDay && (
            <p className="font-serif text-sm font-semibold text-primary">
              {formatDate(selectedDay)} — Program Detayı
            </p>
          )}
          {filteredSchedule.length === 0 ? (
            <Card className="text-center text-sm text-muted-foreground">
              Bu gün için program kaydı yok. Takvimden programı olan bir gün seçin veya yeni satır ekleyin.
            </Card>
          ) : (
            filteredSchedule.map((item) => {
              const timeStatus = getScheduleTimeStatus(item, now);
              return (
                <Card key={item.id} className={scheduleCardClass(timeStatus)}>
                  {editingId === item.id ? (
                    <ScheduleItemForm
                      dayDate={dayDate}
                      form={form}
                      setForm={setForm}
                      setDayDate={setDayDate}
                      editingId={editingId}
                      error={error}
                      loading={loading}
                      onSubmit={saveItem}
                      onCancel={resetForm}
                      minDate={minDate}
                      maxDate={maxDate}
                    />
                  ) : (
                    <ScheduleItemDisplay
                      item={item}
                      timeStatus={timeStatus}
                      onEdit={() => startEdit(item)}
                      onDelete={() => deleteItem(item.id)}
                  duzenlenebilir={yonetebilir}
                    />
                  )}
                </Card>
              );
            })
          )}
        </div>
      ) : (
        filteredSchedule.map((item) => {
          const timeStatus = getScheduleTimeStatus(item, now);
          return (
            <Card key={item.id} className={scheduleCardClass(timeStatus)}>
              {editingId === item.id ? (
                <ScheduleItemForm
                  dayDate={dayDate}
                  form={form}
                  setForm={setForm}
                  setDayDate={setDayDate}
                  editingId={editingId}
                  error={error}
                  loading={loading}
                  onSubmit={saveItem}
                  onCancel={resetForm}
                  minDate={minDate}
                  maxDate={maxDate}
                />
              ) : (
                <ScheduleItemDisplay
                  item={item}
                  showDate
                  timeStatus={timeStatus}
                  onEdit={() => startEdit(item)}
                  onDelete={() => deleteItem(item.id)}
                  duzenlenebilir={yonetebilir}
                />
              )}
            </Card>
          );
        })
      )}
    </div>
  );
}
